'use client';

import { LDProvider } from 'launchdarkly-react-client-sdk';
import { type PropsWithChildren } from 'react';

import { flagsContext } from '@/contexts/flagsContext';

import { useEnvironment } from './useEnvironment';
import { useUser } from './useUser';

export const LaunchDarklyProvider = (props: PropsWithChildren) => {
  const { isDev, isDemo, isTest, launchDarklyClientId } = useEnvironment();
  const { currentProjectId, user } = useUser();
  if (!currentProjectId) {
    return props.children;
  }

  if (isDemo || isDev || isTest) {
    return (
      <flagsContext.Provider
        value={{
          flags: {
            // Sivan
            AllowAccountLevelSamlWhenOrgAttached: false,

            // Amir
            NonCognitoEonAccounts: false,
            SkipOnboarding: false,
            dlsgFrontend: false,
            LlgFrontend: false,
            ResourceSizeConditionEnabled: true,

            // Matan
            InventoryFilterOmnibox: true,
            // LakehouseBaseUrl: 'http://localhost:3003',

            // Erez
            // permanent
            enableHourlyBackup: false, // opened only for Fanatics
            AzureSqlVirtualMachineSupport: true,
            AzureSqlManagedInstanceSupport: true,
            MySqlFlexibleServerSupport: true,
            ownCustomerOnboardingEnabled: false,
            byovEnabled: true,
            hourlyDlsgPolicyEnabled: false,
            // dynamic
            GcpRestoreOrgAndFolderSupport: true,
            SecurityFindings: true,
            ClassificationPoliciesEnabled: true,

            // Yaara
            EnableGDSGPage: isDev,
            EnableGdsgNotifications: false,
            StorageGatewayCostExplorer: isDev,
            SaasBackupCostExplorer: isDev,
            EnableGdsgObjectActions: isDev,
            EnableGdsgInventoryReports: isDev,
            SupportColdTierVaults: true,

            // Or
            AiAssistantSidebar: isDev,

            // Moshe
            SapHanaPitrSupport: true,
            MongoAtlasSupport: true,

            // Ilan - Mongo Atlas Restore
            MongoAtlasAdvancedRestoreEnabled: isDev,

            // Ilya
            ExploreFilesForDatabaseVms: true,

            // Keren
            RdsRestoreWithoutEncryptionEnabled: false,
            RecordRestoreResultsEnabled: true,

            // Dvir
            GcpVmHighFrequencyBackupEnabled: true,
            BypassSourceAccountRequirement: false,

            // Abir
            RdsHighFrequencyBackupEnabled: true,

            // Eldar
            // false matches the backend's LocalValue; true would block every
            // cross-cloud Azure-vault eligibility path (including the DB flags below)
            EnableAzureVaultNetworkIsolation: false,
            // Nitzan
            EnableCrossCloudDbBackup: true,
            EnableCrossCloudDbBackupToAzureVault: true,
            EnableCrossCloudDynamoKeyspacesBackup: true,
            EnableCrossCloudDynamoKeyspacesBackupToAzureVault: true,

            // Ariel
            AzureSnapshotsDiscoverySupport: true,

            // Barak
            AzureMssqlTdeCmkRestore: true,

            // Ran
            AwsSelectiveOnboarding: true,
            SkipOrgUnitInvokeDiscovery: true,
            AwsRdsNativeBackupSupport: true,
            AwsEksClusterBackupSupport: true,
            // Tzachi
            AzureMultipleAppRegistrations: false,
            AzureCustomPermissionsModel: false,

            // Google Workspace
            GoogleWorkspaceSupport: true,
            GoogleWorkspaceEnableRansomwareDetection: true,
            GwsInPlaceRestoreOnly: false,
            GwsRestoreToTargetMailbox: true,
            GwsRestoreToTargetDrive: true,
            GwsRestoreWholeMailbox: true,
            GwsRestoreWholeDrive: true,
            GwsRestoreWholeSharedDrive: true,
            GwsRestoreWholeGroup: true,
            GwsRestoreWholeUser: true,

            // Microsoft 365
            Microsoft365Support: true,
            Microsoft365RestoreSupport: true,
            EnableRansomwareDetectionM365: true,
            Microsoft365SaasEntitiesExplorer: false,

            // Entra ID (IDP track)
            EntraIdSupport: true,

            // Eden
            EnableMrapForByov: true,

            // Oryan
            BigQueryNativeRestoreEnabled: true,
            EnableBigQueryNativePolicy: true,
            GcpProjectLabelTagFilterEnabled: true,
            CancelBackupJobEnabled: true,
            BigQueryRestoreOverwriteEnabled: true,

            // Benji
            SensitivityAnnotations: true,
            EnableNetworkClassification: true,

            // Liran - RDS Restore
            RdsRestorePreserveOriginalMemberNames: false,
            AwsKeyspacesSupport: true,
            CassandraSupport: true,

            // Liran - StreamLake
            // false keeps local dev gated by the demo-settings Data Platform toggle
            DataPlatformConsoleEnabled: false,

            // MatanR
            RdsCdcUiEnabled: true,

            // Eyal
            RolePermissionFamiliesEnabled: false,

            // Or
            AiAgents: isDev,
            AiDashboardsSupport: isDev,

            // Yair
            ZeroEtlEnabled: true,
            LakehouseEnabled: false,
            LakehouseDataQualityUiEnabled: true,
            ZeroEtlMultiResourcePolicies: false,
            ZeroEtlBackupPolicyLink: true,
            DataKnowledgeGraphEnabled: false,
            AgenticDataLayerEnabled: false,
            // `true` offers every Lakehouse-backed Data Platform screen; an
            // array of screen keys offers exactly those. See
            // `libs/streamlake/screens.ts`.
            StreamlakeLakehouseScreens: true,
            // Storage growth / Spend charts on the pipeline overview; off until
            // the control plane's cost and table-size gauges are trustworthy.
            StreamlakePipelineCostChartsEnabled: false,

            // Daniel - Export access-denied items
            EnableExportAccessDeniedItems: true,
            InventoryIssuesColumn: false,

            // Lior
            RestoreTemplates: true,
            BulkRestore: true,
            EnableYaraScan: true,
            RemediationOrchestration: true,

            // Jonathan - Inventory entities filter
            EnableInventoryEntitiesFilter: true,
            DataClassificationCredentialsEnabled: false,

            // Zohar - SaaS inventory default landing page
            SaasInventoryDefaultLanding: false,

            // Ilan K
            ZeroEtlJobSyncRowCountsEnabled: true,
            BackupComplianceJson: {
              view: true,
              trend: true,
              export: true,
              audit: false,
            },

            // Setter
            UseTargetVpcForRestoreServer: true,
            UseTargetVpcForFileRestoreServer: true,
          },
          flagKeyMap: {},
          ldClient: undefined,
        }}
      >
        {props.children}
      </flagsContext.Provider>
    );
  }

  if (!launchDarklyClientId || !user?.eonAccount) {
    return props.children;
  }

  return (
    <LDProvider
      reactOptions={{
        reactContext: flagsContext,
        useCamelCaseFlagKeys: false,
      }}
      clientSideID={launchDarklyClientId}
      timeout={2}
      context={{
        kind: 'multi',
        user: {
          key: currentProjectId, // backward compatibility
          // name: user.givenName,
          // email: user.email,
          // id: user.id,
          name: user.eonAccount.name,
          domain: user.eonAccount.domain,
        },
        organization: {
          key: user.eonAccount.id,
          name: user.eonAccount.name,
          domain: user.eonAccount.domain,
          projectId: currentProjectId,
        },
      }}
      options={{ streaming: false }}
    >
      {props.children}
    </LDProvider>
  );
};
